import {
	Announcements,
	closestCenter,
	DndContext,
	DragEndEvent,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
} from "@dnd-kit/core";
import { restrictToParentElement, restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
	arrayMove,
	SortableContext,
	sortableKeyboardCoordinates,
	verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { Columns3 } from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import SortableColumnRow, { PinnedColumnRow } from "./SortableColumnRow";
import { ListColumns } from "./useListColumns";

interface Props<T> {
	columns: ListColumns<T>;
}

/**
 * The column-settings panel for a virtualized list page: one row per available
 * column with a drag grip, a visibility checkbox and the label. Dragging a row
 * changes the table's left-to-right column order, the checkbox shows/hides the
 * column, and hidden columns stay dimmed in place — so showing one again puts it
 * back where the user left it. Both are persisted per user and per list.
 *
 * The pinned first column (the row anchor) sits on top with a lock and is
 * rendered outside the sortable context, so it can neither be moved nor be a
 * drop target. The footer resets order, visibility and widths in one go.
 *
 * A popover rather than a dropdown-menu: menu items own pointer and keyboard
 * handling (roving focus, typeahead, close-on-activate), which fights the
 * drag-and-drop sensors. Rendered left of the filter button in
 * {@link ListPageToolbar}.
 */
function ColumnSettingsMenu<T>({ columns }: Props<T>) {
	const { t } = useTranslation();
	const { allColumns, isVisible, toggle, reorder, resetToDefault, hasOverrides } = columns;

	const pinned = allColumns.find((c) => c.pinned);
	const sortableColumns = useMemo(() => allColumns.filter((c) => !c.pinned), [allColumns]);
	const persistedKeys = useMemo(() => sortableColumns.map((c) => c.key), [sortableColumns]);

	// The order shown while a drop is being persisted, so the list doesn't flicker
	// back before the optimistic preferences update lands. Cleared as soon as the
	// persisted order changes.
	const [localKeys, setLocalKeys] = useState<string[] | null>(null);
	const persistedSignature = persistedKeys.join("|");
	useEffect(() => setLocalKeys(null), [persistedSignature]);

	const orderedKeys = localKeys ?? persistedKeys;
	const columnByKey = useMemo(
		() => new Map(sortableColumns.map((c) => [c.key, c])),
		[sortableColumns]
	);

	// Announcements read the order as it is when they fire, not at render time.
	const orderRef = useRef(orderedKeys);
	orderRef.current = orderedKeys;
	const labelOf = (key: string) => {
		const col = columnByKey.get(key);
		return col ? t(col.label as any) : key;
	};
	const positionOf = (key: string) => orderRef.current.indexOf(key) + 1;

	const sensors = useSensors(
		// A few pixels of movement before a drag starts, so clicking the grip (e.g.
		// to focus it for keyboard reordering) doesn't pick the row up.
		useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
		useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
	);

	const announcements: Announcements = {
		onDragStart: ({ active }) =>
			t("COLUMNS_DND_PICKED_UP", {
				name: labelOf(String(active.id)),
				position: positionOf(String(active.id)),
				total: orderRef.current.length,
			}),
		onDragOver: ({ active, over }) =>
			// dnd-kit fires an initial "over itself" event right after pick-up; letting
			// it through would immediately overwrite the pick-up announcement.
			over && over.id !== active.id
				? t("COLUMNS_DND_OVER", {
						name: labelOf(String(active.id)),
						position: positionOf(String(over.id)),
						total: orderRef.current.length,
				  })
				: undefined,
		onDragEnd: ({ active, over }) =>
			over
				? t("COLUMNS_DND_DROPPED", {
						name: labelOf(String(active.id)),
						position: positionOf(String(over.id)),
						total: orderRef.current.length,
				  })
				: undefined,
		onDragCancel: ({ active }) => t("COLUMNS_DND_CANCELLED", { name: labelOf(String(active.id)) }),
	};

	// Escape cancels an in-progress keyboard drag; without this the same keypress
	// would also dismiss the popover (Radix listens for it on the document).
	const draggingRef = useRef(false);

	const onDragEnd = ({ active, over }: DragEndEvent) => {
		draggingRef.current = false;
		if (!over || active.id === over.id) return;
		const from = orderedKeys.indexOf(String(active.id));
		const to = orderedKeys.indexOf(String(over.id));
		if (from < 0 || to < 0) return;

		const next = arrayMove(orderedKeys, from, to);
		setLocalKeys(next);
		reorder(pinned ? [pinned.key, ...next] : next);
	};

	return (
		<Popover>
			<PopoverTrigger asChild>
				<button
					className="flex h-[33px] w-10 items-center justify-center rounded-md border border-solid border-border bg-white hover:border-action"
					title={t("COLUMNS")}
				>
					<Columns3 className="h-4 w-4" />
				</button>
			</PopoverTrigger>
			<PopoverContent
				align="end"
				className="max-h-[70vh] w-64 overflow-y-auto p-2"
				onEscapeKeyDown={(e) => {
					if (draggingRef.current) e.preventDefault();
				}}
			>
				<div className="px-1 pb-2">
					<div className="text-sm font-semibold">{t("COLUMNS")}</div>
					<div className="text-xs text-muted-foreground">{t("COLUMNS_DRAG_HINT")}</div>
				</div>

				{pinned && <PinnedColumnRow columnKey={pinned.key} label={t(pinned.label as any)} />}

				<DndContext
					sensors={sensors}
					collisionDetection={closestCenter}
					modifiers={[restrictToVerticalAxis, restrictToParentElement]}
					accessibility={{
						announcements,
						screenReaderInstructions: { draggable: t("COLUMNS_DND_INSTRUCTIONS") },
					}}
					onDragStart={() => (draggingRef.current = true)}
					onDragCancel={() => (draggingRef.current = false)}
					onDragEnd={onDragEnd}
				>
					<SortableContext items={orderedKeys} strategy={verticalListSortingStrategy}>
						<div>
							{orderedKeys.map((key) => {
								const col = columnByKey.get(key);
								if (!col) return null;
								return (
									<SortableColumnRow
										key={key}
										columnKey={key}
										label={t(col.label as any)}
										checked={isVisible(key)}
										locked={col.mandatory}
										onToggle={() => toggle(key)}
									/>
								);
							})}
						</div>
					</SortableContext>
				</DndContext>

				<div className="mt-2 border-t border-border pt-2">
					<button
						type="button"
						disabled={!hasOverrides}
						onClick={resetToDefault}
						className="w-full rounded-md px-1 py-1 text-left text-sm text-action hover:bg-accent disabled:cursor-default disabled:text-muted-foreground disabled:hover:bg-transparent"
					>
						{t("COLUMNS_RESET")}
					</button>
				</div>
			</PopoverContent>
		</Popover>
	);
}

export default ColumnSettingsMenu;
